import * as _ from 'lodash';
import * as moment from 'moment';
import { useMemo, useState } from 'react';
import { IKidneyAvailOnTimeReports } from '@nkr/store/index';
import { IKidneyAvailOnTimeReport, IKidneyAvailOnTimeReportTotals } from '@nkr/models/index';
import { GridWrapper } from './styles';
import { Utility } from '@nkr/services/utility';
import { AgGridReact, AgGridColumnProps, AgGridColumnGroupProps } from 'ag-grid-react';
import {
    IHeaderParams, GridApi, GridReadyEvent, ICellRendererParams,
    ValueGetterParams, FirstDataRenderedEvent,
    GridSizeChangedEvent,
    RowDataChangedEvent
} from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';

type MyColumn = AgGridColumnProps | AgGridColumnGroupProps;

interface IProps {
    show: boolean,
    report: IKidneyAvailOnTimeReports
}

const KidneyAvailOnTimeReportGrid = (props: IProps) => {
    const [gridApi, setGridApi] = useState<GridApi>(null);

    const onGridReady = (e: GridReadyEvent) => {
        setGridApi(e.api);

        e.api.sizeColumnsToFit();
    };

    const getTotals = (data: IKidneyAvailOnTimeReportTotals[]): IKidneyAvailOnTimeReportTotals => {
        let totalTXPs = _.sumBy(data, item => item.txPs);
        let totalNotLate = _.sumBy(data, item => item.notLate);
        let totalLate = _.sumBy(data, item => item.late);
        let totalVeryLate = _.sumBy(data, item => item.veryLate);
        let totalLateVeryLate = _.sumBy(data, item => item.latePlusVeryLate);
        let totaltotalNotLatePercent = totalTXPs > 0 ? (totalNotLate / totalTXPs) * 100 : 0;
        let totaltotalLatePercent = totalTXPs > 0 ? (totalLate / totalTXPs) * 100 : 0;
        let totaltotalVeryLatePercent = totalTXPs > 0 ? (totalVeryLate / totalTXPs) * 100 : 0;
        let totaltotalLateVeryLatePercent = totalTXPs > 0 ? (totalLateVeryLate / totalTXPs) * 100 : 0;

        return {
            Id: 0,
            dCenter: 'Totals/Average',
            txPs: totalTXPs,
            notLate: totalNotLate,
            late: totalLate,
            veryLate: totalVeryLate,
            latePlusVeryLate: totalLateVeryLate,
            notLatePercent: _.round(totaltotalNotLatePercent, 1),
            latePercent: _.round(totaltotalLatePercent, 1),
            veryLatePercent: _.round(totaltotalVeryLatePercent, 1),
            latePlusVeryLatePercent: _.round(totaltotalLateVeryLatePercent, 1)
        };
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        // get the current grids width
        let gridWidth = document.getElementById('grid-wrapper').offsetWidth;

        // keep track of which columns to hide/show
        let columnsToShow = [];
        let columnsToHide = [];

        // iterate over all columns (visible or not) and work out
        // now many columns can fit (based on their minWidth)
        let totalColsWidth = 0;
        let allColumns = e.columnApi.getAllColumns();

        for (let i = 0; i < allColumns.length; i++) {
            let column = allColumns[i];
            totalColsWidth += column.getMinWidth();

            if (totalColsWidth > gridWidth) {
                columnsToHide.push(column.getColId);
            } else {
                columnsToShow.push(column.getColId);
            }
        }

        // show/hide columns based on current grid width
        e.columnApi.setColumnsVisible(columnsToShow, true);
        e.columnApi.setColumnsVisible(columnsToHide, false);

        // fill out any available space to ensure there are no gaps
        e.api.sizeColumnsToFit();
    };

    const reportColumns: MyColumn[] = [
        {
            colId: 'Rank',
            field: 'rank',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    R#
                </SortableHeaderComponent>,
            headerName: 'Rank',
            width: 55,
            sortable: false,
            resizable: true,
            suppressSizeToFit: true,
            valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
        },
        {
            colId: 'transplantedDate',
            field: 'transplantedDate',
            headerName: 'Date Transplanted',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Date Transplanted
                </SortableHeaderComponent>,
            width: 130,
            cellRendererFramework: (props: ICellRendererParams) => {
                return moment(props.value).format('MM/DD/YYYY');
            }
        },
        {
            colId: 'swapNumber',
            field: 'swapNumber',
            headerName: 'S',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Swap #
                </SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IKidneyAvailOnTimeReport;
                return data.swapNumber + '/' + data.clusterNumber + '/' + data.position;
            },
            width: 120,
        },
        {
            colId: 'recipientCenter',
            field: 'recipientCenter',
            headerName: 'R Center',
            comparator: Utility.nameComparator,
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient Center
                </SortableHeaderComponent>,
            width: 150,
        },
        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            headerName: 'Recipient Alias',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient Alias
                </SortableHeaderComponent>,
            width: 150,
        },
        {
            colId: 'donorCenter',
            field: 'donorCenter',
            headerName: 'Donor Center',
            comparator: Utility.nameComparator,
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor Center
                </SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IKidneyAvailOnTimeReport;

                if (data.remoteDonorCenter != null) {
                    return (
                        <>{data.donorCenter} (<span style={{ color: 'red' }}>{data.remoteDonorCenter}</span>)</>
                    );
                }

                return (
                    <>{data.donorCenter}</>
                );
            },
            width: 150,
        },
        {
            colId: 'donorAlias',
            field: 'donorAlias',
            headerName: 'Donor Alias',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor Alias
                </SortableHeaderComponent>,
            width: 150,
        },
        {
            colId: 'timeExpected',
            field: 'timeExpected',
            headerName: 'Expected',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Expected
                </SortableHeaderComponent>,
            width: 150,
        },

        {
            colId: 'timeActual',
            field: 'timeActual',
            headerName: 'Actual',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Actual
                </SortableHeaderComponent>,
            width: 120,
        },

        {
            colId: 'minuteDifference',
            field: 'minuteDifference',
            headerName: 'Minute  Δ',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Minute Δ
                </SortableHeaderComponent>,
            width: 120,
        },

        {
            colId: 'lateness',
            field: 'lateness',
            headerName: 'Lateness',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Lateness
                </SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IKidneyAvailOnTimeReport;

                return (
                    <>{data.lateness}</>
                );
            },
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IKidneyAvailOnTimeReport;
                return data.latenessSort
            },
            width: 120,
        }
    ];

    const reportColumnDefs = useMemo(() => reportColumns, []);

    const reportTotalsColumns: MyColumn[] = [
        {
            colId: 'donorCenter',
            field: 'donorCenter',
            headerName: 'Donor Center',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Donor Center
                </SortableHeaderComponent>,

            width: 120,
        },
        {
            colId: 'transplants',
            field: 'transplants',
            headerName: 'Transplants',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Transplants
                </SortableHeaderComponent>,
            width: 120,
        },
        {
            colId: 'notLate',
            field: 'notLate',
            headerName: 'Not Late',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Not Late
                </SortableHeaderComponent>,
            width: 100,
        },
        {
            colId: 'late',
            field: 'late',
            headerName: '% Invalid',
            width: 170,
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Late
                </SortableHeaderComponent>,
        },
        {
            colId: 'veryLate',
            field: 'veryLate',
            headerName: 'Very Late',
            width: 170,
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Very Late
                </SortableHeaderComponent>,
        },
        {
            colId: 'latePlusVeryLate',
            field: 'latePlusVeryLate',
            headerName: 'Late Plus Very Late',
            width: 170,
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Late Plus Very Late
                </SortableHeaderComponent>,
        },
        {
            colId: 'notLatePercent',
            field: 'notLatePercent',
            headerName: '% Not Late',
            width: 190,
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    % Not Late
                </SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                return (
                    <>{params.value}%</>
                )
            }
        },
        {
            colId: 'latePercent',
            field: 'latePercent',
            headerName: '% Late',
            width: 190,
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    % Late
                </SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                return (
                    <>{params.value}%</>
                )
            }
        },
        {
            colId: 'veryLatePercent',
            field: 'veryLatePercent',
            headerName: '% Very Late',
            width: 190,
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    % Very Late
                </SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                return (
                    <>{params.value}%</>
                )
            }
        },
        {
            colId: 'latePlusVeryLatePercent',
            field: 'latePlusVeryLatePercent',
            headerName: '% Late Plus Very Late',
            width: 190,
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    % Late Plus Very Late
                </SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                return (
                    <>{params.value}%</>
                )
            }
        }
    ];

    const reportTotalsColumnDefs = useMemo(() => reportTotalsColumns, []);

    if (props.report == null)
        return null;

    const totals = getTotals(props.report.kidneyAvailOnTimeTotals);

    return (
        <>
            <>
                <h4 style={{ margin: '0px 0px 10px 0px' }}>Detail View</h4>
                <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap' style={{ marginBottom: 20, height: props.show ? 500 : null }}>
                    <AgGridReact
                        defaultColDef={{
                            cellClass: 'center-list-cell',
                            headerClass: 'center-list-header',
                            sortable: true,
                            resizable: true,
                        }}
                        columnDefs={reportColumnDefs}
                        rowData={props.report.kidneyAvailOnTime}
                        enableCellTextSelection={true}
                        enableRangeSelection={true}
                        noRowsOverlayComponentFramework={() =>
                            <div>No data for the selected date range...</div>}
                        onGridReady={onGridReady}
                        onGridSizeChanged={onGridSizeChanged}
                        onFirstDataRendered={(e: FirstDataRenderedEvent) => e.api.sizeColumnsToFit()}
                        onRowDataChanged={(e: RowDataChangedEvent) => e.api.sizeColumnsToFit()}
                        suppressCellSelection={true}
                        onSortChanged={() => {
                            gridApi.refreshCells();
                        }}
                    />
                </GridWrapper>
            </>

            {props.show && (
                <>
                    <h4 style={{ margin: '0px 0px 10px 0px' }}>Summary View</h4>
                    <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap' style={{ marginBottom: 20, height: 500 }}>
                        <AgGridReact
                            defaultColDef={{
                                cellClass: 'center-list-cell',
                                headerClass: 'center-list-header',
                                sortable: true
                            }}
                            columnDefs={reportTotalsColumnDefs}
                            rowData={props.report.kidneyAvailOnTimeTotals}
                            enableCellTextSelection={true}
                            enableRangeSelection={true}
                            noRowsOverlayComponentFramework={() =>
                                <div>No data for the selected date range...</div>}
                            onGridReady={onGridReady}
                            onGridSizeChanged={onGridSizeChanged}
                            onFirstDataRendered={(props: FirstDataRenderedEvent) => props.api.sizeColumnsToFit()}
                            suppressCellSelection={true}
                            getRowStyle={(props: any) => {
                                let numericValue = +props.data.latePlusVeryLatePercent;

                                if (numericValue > 25 && numericValue <= 50) {
                                    return { backgroundColor: 'yellow' } as any;
                                }

                                if (numericValue > 50) {
                                    return { backgroundColor: 'red', color: 'white' } as any;
                                }

                                if (props.node.rowPinned == 'bottom') {
                                    return { backgroundColor: 'lightblue' };
                                }
                            }}
                            onSortChanged={() => {
                                gridApi.refreshCells();
                            }}
                            pinnedBottomRowData={
                                [{
                                    rank: 0, dCenter: totals.dCenter, txPs: totals.txPs, notLate: totals.notLate, late: totals.late,
                                    veryLate: totals.veryLate, latePlusVeryLate: totals.latePlusVeryLate, notLatePerc: totals.notLatePercent, latePerc: totals.latePercent,
                                    veryLatePerc: totals.veryLatePercent, latePlusVeryLatePerc: totals.latePlusVeryLatePercent
                                }]
                            }
                        />
                    </GridWrapper>
                </>
            )}
        </>
    );
}

export default KidneyAvailOnTimeReportGrid;